import type { App, Project } from '@hangar/shared';
import { useApps, useProjects } from '@/api/hooks';

export type AppPageData =
  | { kind: 'loading' }
  | { kind: 'error'; error: unknown; onRetry: () => void }
  | { kind: 'missing'; project: Project | undefined }
  | { kind: 'ready'; project: Project; app: App };

interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  data: unknown;
  error: unknown;
  refetch: () => unknown;
}

const loadingFirstTime = (q: QueryLike) => q.isLoading && !q.data;
const failedFirstTime = (q: QueryLike) => q.isError && !q.data;

/** The project and the app of the URL, or why there is none yet: loading, a failed request, or not found. */
export function useAppPageData(slug: string, appSlug: string): AppPageData {
  const projects = useProjects();
  const project = projects.data?.find((p) => p.slug === slug);
  const apps = useApps(project?.id);
  const app = apps.data?.find((a) => a.slug === appSlug);
  if (loadingFirstTime(projects) || (project && loadingFirstTime(apps))) return { kind: 'loading' };
  const failed = [projects, apps].find(failedFirstTime);
  if (failed) return { kind: 'error', error: failed.error, onRetry: () => void failed.refetch() };
  return project && app ? { kind: 'ready', project, app } : { kind: 'missing', project };
}
